import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { supabase } from '@/lib/supabase/client'
import type { Database } from '@/lib/supabase/types'

export type ProgressRow = Database['public']['Tables']['progress']['Row']

interface ProgressState {
  progressMap: Record<string, ProgressRow>
  isLoading: boolean
  error: string | null
  fetchProgress: (userId: string) => Promise<void>
  markCompleted: (
    userId: string,
    algoId: string,
    chapterId: string,
    xpEarned: number,
    stars: number,
    bestTimeMs?: number
  ) => Promise<void>
}

export const useProgressStore = create<ProgressState>()(
  devtools(
    (set, get) => ({
      progressMap: {},
      isLoading: false,
      error: null,

      fetchProgress: async (userId) => {
        set({ isLoading: true, error: null })
        const { data, error } = await supabase
          .from('progress')
          .select('*')
          .eq('user_id', userId)

        if (error) {
          set({ error: error.message, isLoading: false })
          return
        }

        const map: Record<string, ProgressRow> = {}
        for (const row of data ?? []) {
          map[row.algo_id] = row
        }
        set({ progressMap: map, isLoading: false })
      },

      markCompleted: async (userId, algoId, chapterId, xpEarned, stars, bestTimeMs) => {
        const existing = get().progressMap[algoId]

        const upsertPayload = {
          user_id: userId,
          algo_id: algoId,
          chapter_id: chapterId,
          stars: Math.max(stars, existing?.stars ?? 0),
          xp_earned: Math.max(xpEarned, existing?.xp_earned ?? 0),
          attempts: (existing?.attempts ?? 0) + 1,
          best_time_ms:
            bestTimeMs !== undefined
              ? existing?.best_time_ms != null
                ? Math.min(bestTimeMs, existing.best_time_ms)
                : bestTimeMs
              : existing?.best_time_ms ?? null,
          completed_at: new Date().toISOString(),
        }

        const { data, error } = await supabase
          .from('progress')
          .upsert(upsertPayload)
          .select()
          .single()

        if (!error && data) {
          set((s) => ({
            progressMap: { ...s.progressMap, [algoId]: data },
          }))
        }
      },
    }),
    { name: 'progress-store' }
  )
)
